import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, ShoppingCart, MapPin, User, LogOut, Clock, CheckCircle, QrCode, CreditCard, AlertCircle, MessageSquare, XCircle, Eye, TrendingUp } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AccountSettings from '@/components/AccountSettings';
import { ClientPickupQRCode } from '@/components/buyer/ClientPickupQRCode';
import PaymentMethodsManager from '@/components/buyer/PaymentMethodsManager';
import DisputesManager from '@/components/buyer/DisputesManager';
import MessagingCenter from '@/components/buyer/MessagingCenter';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import CreateDisputeDialog from '@/components/buyer/CreateDisputeDialog';
import DefaultPreferences from '@/components/buyer/DefaultPreferences';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { toast } from 'sonner';
import { formatPriceWithCurrency } from '@/utils/currency';
import { useCurrency } from '@/hooks/useCurrency';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Order {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  expires_at: string | null;
  product: {
    title: string;
    images: string[];
  };
  relay: {
    name: string;
    address: string;
  };
  pickup_tokens: {
    qr_svg: string | null;
    otp_expires_at: string | null;
  }[] | null;
}

const Dashboard = () => {
  const { user, profile, signOut } = useAuth();
  const { t } = useLanguage();
  const { displayCurrency, convertPrice } = useCurrency();
  const { getEffectiveUserId, isImpersonating } = useImpersonation();
  const navigate = useNavigate();
  const { unreadCount, markAsSeen } = useUnreadMessages();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQROrder, setSelectedQROrder] = useState<Order | null>(null);
  const [impersonatedProfile, setImpersonatedProfile] = useState<any>(null);
  const [activeTab, setActiveTab] = usePersistentTabState(
    'buyer-dashboard:activeTab',
    'orders',
    ['orders', 'payments', 'disputes', 'messages', 'profile']
  );
  
  // Utiliser l'ID effectif (impersonné ou réel)
  const effectiveUserId = getEffectiveUserId();
  
  // Profil effectif (impersonné ou réel)
  const effectiveProfile = isImpersonating ? impersonatedProfile : profile;
  const effectiveEmail = isImpersonating ? impersonatedProfile?.email : user?.email;

  // Charger le profil impersonné si nécessaire
  useEffect(() => {
    if (isImpersonating && effectiveUserId && effectiveUserId !== user?.id) {
      const fetchImpersonatedProfile = async () => {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', effectiveUserId)
          .single();
        setImpersonatedProfile(data);
      };
      fetchImpersonatedProfile();
    }
  }, [isImpersonating, effectiveUserId, user?.id]);

  useEffect(() => {
    if (effectiveUserId) {
      fetchOrders();
    }
  }, [effectiveUserId]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products(title, images),
          relay:relays(name, address),
          pickup_tokens(qr_svg, otp_expires_at),
          order_items(
            product_id,
            quantity,
            unit_price,
            products(title, images)
          )
        `)
        .eq('client_id', effectiveUserId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching orders:', error);
        return;
      }

      // Ensure pickup_tokens is always an array and get product from order_items if not in product
      const ordersWithArrayTokens = (data || []).map(order => {
        // Get product from direct relation or from order_items
        let productData = order.product;
        if (!productData && order.order_items && order.order_items.length > 0) {
          const firstItem = order.order_items[0];
          productData = firstItem.products;
        }
        
        return {
          ...order,
          product: productData,
          pickup_tokens: Array.isArray(order.pickup_tokens) ? order.pickup_tokens : (order.pickup_tokens ? [order.pickup_tokens] : [])
        };
      });

      setOrders(ordersWithArrayTokens);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDING_PAYMENT': { label: t('order.status.pending_payment'), variant: 'secondary' as const },
      'PAID': { label: t('order.status.paid'), variant: 'default' as const },
      'AWAITING_DROP_OFF': { label: t('order.status.awaiting_drop_off'), variant: 'secondary' as const },
      'AVAILABLE_FOR_PICKUP': { label: t('order.status.available_for_pickup'), variant: 'default' as const },
      'PICKED_UP': { label: t('order.status.picked_up'), variant: 'secondary' as const },
      'EXPIRED': { label: t('order.status.expired'), variant: 'destructive' as const },
      'DISPUTE': { label: t('order.status.dispute'), variant: 'destructive' as const },
      'CANCELLED': { label: t('order.status.cancelled'), variant: 'destructive' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { label: status, variant: 'secondary' as const };
    return <Badge variant={config.variant} className="whitespace-nowrap text-[10px] md:text-xs px-2 py-0.5 flex-shrink-0">{config.label}</Badge>;
  };

  const canCancelOrder = (order: Order): boolean => {
    if (order.status !== 'PAID') return false;
    const createdAt = new Date(order.created_at);
    const now = new Date();
    const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    return diffMinutes < 30;
  };

  const handleCancelOrder = async (orderId: string, orderNumber: string) => {
    try {
      const { data, error } = await supabase.rpc('cancel_order', {
        order_uuid: orderId,
        cancellation_reason: 'Annulation par le client'
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; message?: string };
      if (result?.success) {
        toast.success('Commande annulée avec succès');
        fetchOrders();
      } else {
        toast.error(result?.error || 'Impossible d\'annuler la commande');
      }
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      toast.error('Erreur lors de l\'annulation de la commande');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container py-4 md:py-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {t('dashboard.welcome')}, {effectiveProfile?.full_name?.split(' ')[0] || effectiveEmail?.split('@')[0]}
          </h1>
          <p className="text-white/90">{t('dashboard.tagline', 'Gérez vos commandes et votre compte')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Quick Actions */}
          <div className="lg:col-span-1 space-y-4">
            {/* Account Card */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-pink-400 flex items-center justify-center text-white text-xl font-bold">
                    {effectiveProfile?.full_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{effectiveProfile?.full_name || effectiveEmail}</CardTitle>
                    <p className="text-xs text-muted-foreground truncate">{effectiveEmail}</p>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Quick Stats */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('dashboard.overview', 'Vue d\'ensemble')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <button 
                  onClick={() => navigate('/orders')}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <ShoppingCart className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('dashboard.total_orders')}</p>
                      <p className="text-2xl font-bold">{orders.length}</p>
                    </div>
                  </div>
                </button>

                <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('dashboard.pending_orders')}</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {orders.filter(o => ['AWAITING_DROP_OFF', 'AVAILABLE_FOR_PICKUP'].includes(o.status)).length}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('dashboard.completed_orders')}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {orders.filter(o => o.status === 'PICKED_UP').length}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('dashboard.quick_actions', 'Actions rapides')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/orders')}
                >
                  <Package className="h-4 w-4 mr-2" />
                  {t('dashboard.view_orders', 'Voir mes commandes')}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => navigate('/products')}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {t('dashboard.continue_shopping', 'Continuer mes achats')}
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-red-600 hover:text-red-700" 
                  onClick={signOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('dashboard.logout')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Main Content */}
          <div className="lg:col-span-2">
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v);
                if (v === 'messages') markAsSeen();
              }}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 gap-1 h-auto p-1">
                <TabsTrigger value="orders" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                  <Package className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('orders', 'Commandes')}</span>
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                  <CreditCard className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('payments', 'Paiements')}</span>
                </TabsTrigger>
                <TabsTrigger value="disputes" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                  <AlertCircle className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('disputes', 'Litiges')}</span>
                </TabsTrigger>
                <TabsTrigger value="messages" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2 relative">
                  <MessageSquare className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('messages', 'Messages')}</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center min-w-[16px] px-0.5">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="profile" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                  <User className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('profile', 'Profil')}</span>
                </TabsTrigger>
              </TabsList>

              {/* Orders Tab */}
              <TabsContent value="orders" className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">{t('dashboard.recent_orders', 'Commandes récentes')}</h2>
                  <Button variant="link" onClick={() => navigate('/orders')} className="text-sm">
                    {t('dashboard.view_all', 'Voir tout')} →
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                  </div>
                ) : orders.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent className="pt-6">
                      <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="font-semibold text-lg mb-2">{t('dashboard.no_orders', 'Aucune commande')}</h3>
                      <p className="text-muted-foreground mb-6">{t('dashboard.no_orders_desc', 'Commencez vos achats dès maintenant')}</p>
                      <Button onClick={() => navigate('/products')} size="lg">
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        {t('dashboard.discover_products', 'Découvrir les produits')}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {orders.slice(0, 5).map((order) => (
                      <Card 
                        key={order.id} 
                        className="hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Product Image */}
                            {order.product?.images?.[0] && (
                              <div className="w-20 h-20 flex-shrink-0">
                                <img
                                  src={order.product.images[0]}
                                  alt={order.product?.title || 'Produit'}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              </div>
                            )}
                            
                            {/* Order Info */}
                            <div className="flex-1 min-w-0">
                              {/* Header: Title + Badge */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="font-semibold text-sm line-clamp-2 flex-1 min-w-0">{order.product?.title || 'Produit supprimé'}</h3>
                                {getStatusBadge(order.status)}
                              </div>
                              
                              {/* Order Number */}
                              <p className="text-xs text-muted-foreground mb-2 truncate">
                                #{order.order_number}
                              </p>
                              
                              {/* Relay & Date - Stack on mobile */}
                              <div className="flex flex-col gap-1 mb-3">
                                <div className="flex items-center gap-1 min-w-0 text-xs text-muted-foreground">
                                  <MapPin className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{order.relay?.name || 'Point relais non défini'}</span>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3 flex-shrink-0" />
                                  <span>{new Date(order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                </div>
                              </div>

                              {/* Price & Action */}
                              <div className="flex items-center justify-between gap-2 mt-1">
                                <p className="font-bold text-sm md:text-base flex-shrink-0">
                                  {formatPriceWithCurrency(order.amount_cents || 0, order.currency || 'CDF', displayCurrency, convertPrice)}
                                </p>
                                
                                {['PAID', 'AVAILABLE_FOR_PICKUP'].includes(order.status) && (
                                  <Button 
                                    size="sm" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedQROrder(order);
                                    }}
                                    variant="outline"
                                    className="h-7 px-2 py-0 gap-1 flex-shrink-0"
                                  >
                                    <QrCode className="h-3.5 w-3.5" />
                                    <span className="text-[10px] md:text-xs">
                                      {order.status === 'AVAILABLE_FOR_PICKUP' ? 'QR' : 'Statut'}
                                    </span>
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {orders.length > 5 && (
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => navigate('/orders')}
                      >
                        {t('dashboard.view_all_orders', 'Voir toutes les commandes')} ({orders.length})
                      </Button>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Payment Methods Tab */}
              <TabsContent value="payments">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      {t('dashboard.payment_methods', 'Moyens de paiement')}
                    </CardTitle>
                    <CardDescription>
                      {t('dashboard.payment_methods_desc', 'Gérez vos méthodes de paiement')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PaymentMethodsManager />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Disputes Tab */}
              <TabsContent value="disputes">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      {t('dashboard.disputes', 'Litiges')}
                    </CardTitle>
                    <CardDescription>
                      {t('dashboard.disputes_desc', 'Suivez vos réclamations')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DisputesManager />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Messages Tab */}
              <TabsContent value="messages">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      {t('dashboard.messages', 'Messages')}
                    </CardTitle>
                    <CardDescription>
                      {t('dashboard.messages_desc', 'Communiquez avec les vendeurs')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MessagingCenter />
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Profile Tab - Account Settings */}
              <TabsContent value="profile" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      {t('dashboard.account_settings', 'Paramètres du compte')}
                    </CardTitle>
                    <CardDescription>
                      {t('dashboard.account_settings_desc', 'Gérez vos informations personnelles')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <DefaultPreferences />
                    <AccountSettings />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      <Footer />
      
      {selectedQROrder && (
        <ClientPickupQRCode
          open={!!selectedQROrder}
          onClose={() => setSelectedQROrder(null)}
          orderId={selectedQROrder.id}
          orderNumber={selectedQROrder.order_number}
          productTitle={selectedQROrder.product?.title || 'Produit'}
          orderStatus={selectedQROrder.status}
        />
      )}
    </div>
  );
};

export default Dashboard;